import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CalStateChip, Countdown, VetoControls } from "./agentic-ui";
import {
  CAL_STATE_CLASS,
  CAL_STATE_LABEL,
  PLAN_CHANNELS,
  addDays,
  calStateOf,
  channelLabel,
  entryDayKey,
  entryFromItem,
  findSlot,
  parseDayKey,
  publishAfterOf,
  startOfWeek,
  useCalendar,
  usePlans,
  type CalState,
  type CalendarEntry,
  type PlanChannel,
  type PlanSlot,
} from "./agentic";
import { Chip } from "./chip";
import { useMinWidth } from "./checks-panel";
import { dayKey, fmtDateTime, fmtDayHeading, isRejected, type StudioItem } from "./helpers";
import { EmptyState } from "./item-ui";
import { SlotDialog, orderingSentence } from "./slot-dialog";

type View = "month" | "week";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const LEGEND: CalState[] = ["planned", "ordered", "waiting", "held", "published", "verified", "pulled_back"];
const CHANNEL_ORDER = [...PLAN_CHANNELS, "ad"] as string[];

function channelRank(c: string): number {
  const i = CHANNEL_ORDER.indexOf(c.toLowerCase());
  return i === -1 ? CHANNEL_ORDER.length : i;
}

function sortEntries(a: CalendarEntry, b: CalendarEntry): number {
  const ta = a.publishAfter ? new Date(a.publishAfter).getTime() : 0;
  const tb = b.publishAfter ? new Date(b.publishAfter).getTime() : 0;
  return channelRank(a.channel) - channelRank(b.channel) || ta - tb || a.title.localeCompare(b.title);
}

function rangeFor(view: View, cursor: Date): { start: Date; end: Date } {
  if (view === "week") {
    const start = startOfWeek(cursor);
    return { start, end: addDays(start, 6) };
  }
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  return { start: startOfWeek(first), end: addDays(startOfWeek(last), 6) };
}

/** An entry with the live item folded in, so Hold / Release act on the real row. */
function itemFor(entry: CalendarEntry, items: StudioItem[]): StudioItem | null {
  if (entry.contentItemId == null) return null;
  const item = items.find((i) => i.id === entry.contentItemId);
  if (!item) return null;
  return { ...item, vetoState: entry.vetoState ?? item.vetoState, publishAfter: (entry.publishAfter as any) ?? item.publishAfter };
}

// ── One event, as a full row ────────────────────────────────────────────────

function EventRow({
  entry,
  item,
  onOpen,
}: {
  entry: CalendarEntry;
  item: StudioItem | null;
  onOpen: (e: CalendarEntry) => void;
}) {
  const state = calStateOf(entry);
  const after = entry.publishAfter ? new Date(entry.publishAfter) : item ? publishAfterOf(item) : null;
  const openable = entry.contentItemId != null || entry.kind === "slot";
  return (
    <div
      className="flex flex-col gap-2 rounded-xl border border-border bg-card/60 p-3 sm:flex-row sm:items-center"
      data-testid={`cal-event-${entry.slotId ?? entry.contentItemId ?? entry.title}`}
      data-state={state}
    >
      <button
        type="button"
        disabled={!openable}
        onClick={() => onOpen(entry)}
        className="min-w-0 flex-1 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60 disabled:cursor-default"
        data-testid="cal-event-open"
      >
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
          <Chip className="border-border bg-muted/40 text-foreground/80">{channelLabel(entry.channel)}</Chip>
          <CalStateChip state={state} label={state === "waiting" && after ? <Countdown until={after} prefix="Publishes in " /> : undefined} />
          {entry.kind === "slot" && state === "planned" && <Chip className="border-border text-muted-foreground">Slot</Chip>}
        </div>
        <p className="line-clamp-2 break-words text-sm font-semibold text-foreground">{entry.title || "Untitled"}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {state === "waiting" && after && `${fmtDateTime(after)}, unless you hold it`}
          {state === "held" && "It will not publish until you release it"}
          {state === "planned" && entry.kind === "slot" && orderingSentence({ date: entryDayKey(entry) })}
          {state === "ordered" && "Draper's team is writing this now"}
          {state === "scheduled" && after && `Goes out ${fmtDateTime(after)}`}
          {state === "needs_fix" && "Needs a fix before it can go out"}
          {state === "pulled_back" && "Published, then taken down by the follow-up check"}
          {state === "verified" && "Published and checked on the live page"}
        </p>
      </button>
      <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
        {item && <VetoControls item={item} size="sm" testIdSuffix="-cal" />}
        {entry.publishedUrl && (
          <a
            href={entry.publishedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-blue-400 underline-offset-2 hover:underline"
            data-testid="cal-link-live"
          >
            View live <ExternalLink size={13} />
          </a>
        )}
      </div>
    </div>
  );
}

// ── Month grid ──────────────────────────────────────────────────────────────

function MonthGrid({
  days,
  month,
  byDay,
  selected,
  onSelect,
  onOpen,
  todayKey,
}: {
  days: Date[];
  month: number;
  byDay: Map<string, CalendarEntry[]>;
  selected: string | null;
  onSelect: (k: string) => void;
  onOpen: (e: CalendarEntry) => void;
  todayKey: string;
}) {
  const wide = useMinWidth(640);
  return (
    <div className="grid grid-cols-7 gap-px overflow-hidden rounded-xl border border-border bg-border text-center" data-testid="cal-month-grid">
      {WEEKDAYS.map((d) => (
        <div key={d} className="bg-muted/40 py-1.5 text-[11px] font-medium text-muted-foreground">{d}</div>
      ))}
      {days.map((date) => {
        const k = dayKey(date);
        const list = byDay.get(k) ?? [];
        const isToday = k === todayKey;
        const dim = date.getMonth() !== month;
        const ring = selected === k ? "ring-2 ring-inset ring-blue-500/70" : isToday ? "ring-1 ring-inset ring-blue-400/60" : "";
        const dayBadge = (
          <span className={`inline-flex min-w-5 items-center justify-center text-xs ${isToday ? "rounded-full bg-blue-600 px-1.5 font-bold text-white" : "text-foreground/80"}`}>
            {date.getDate()}
          </span>
        );
        if (!wide) {
          return (
            <button
              key={k}
              type="button"
              onClick={() => onSelect(k)}
              aria-label={`${date.toLocaleDateString(undefined, { month: "long", day: "numeric" })}${isToday ? ", today" : ""}, ${list.length} item${list.length === 1 ? "" : "s"}`}
              className={`flex min-h-[3.5rem] flex-col items-center gap-1 bg-card/70 p-1 ${dim ? "opacity-45" : ""} ${ring}`}
              data-testid={`cal-day-${k}`}
              data-today={isToday || undefined}
            >
              {dayBadge}
              <span className="flex flex-wrap justify-center gap-0.5">
                {list.slice(0, 4).map((e, i) => (
                  <span key={i} className={`h-2 w-2 rounded-full ${CAL_STATE_CLASS[calStateOf(e)]}`} aria-hidden />
                ))}
              </span>
              {list.length > 4 && <span className="text-[9px] text-muted-foreground">+{list.length - 4}</span>}
            </button>
          );
        }
        return (
          <div key={k} className={`flex min-h-[6.5rem] flex-col items-stretch gap-1 bg-card/70 p-1.5 text-left ${dim ? "opacity-45" : ""} ${ring}`} data-testid={`cal-day-${k}`} data-today={isToday || undefined}>
            <button type="button" onClick={() => onSelect(k)} className="flex items-center gap-1 self-start rounded" aria-label={`${date.toLocaleDateString(undefined, { month: "long", day: "numeric" })}, ${list.length} items`}>
              {dayBadge}
              {isToday && <span className="text-[10px] font-semibold text-blue-300">Today</span>}
            </button>
            {list.slice(0, 3).map((e, i) => {
              const state = calStateOf(e);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => onOpen(e)}
                  title={`${channelLabel(e.channel)}: ${e.title} (${CAL_STATE_LABEL[state]})`}
                  className={`flex w-full items-center gap-1 truncate rounded px-1 py-0.5 text-left text-[10px] leading-tight ${CAL_STATE_CLASS[state]}`}
                  data-testid="cal-chip"
                  data-state={state}
                >
                  <span className="shrink-0 font-semibold">{channelLabel(e.channel)}</span>
                  <span className="truncate">{e.title}</span>
                </button>
              );
            })}
            {list.length > 3 && (
              <button type="button" onClick={() => onSelect(k)} className="self-start text-[10px] text-muted-foreground underline underline-offset-2">
                +{list.length - 3} more
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Tab ─────────────────────────────────────────────────────────────────────

export function CalendarTab({
  items,
  onOpenItem,
  onGoPlan,
}: {
  items: StudioItem[];
  onOpenItem: (id: number) => void;
  onGoPlan: () => void;
}) {
  const [view, setView] = useState<View>(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(min-width: 640px)").matches ? "month" : "week",
  );
  const [cursor, setCursor] = useState(() => new Date());
  const todayKey = dayKey(new Date());
  const [selected, setSelected] = useState<string | null>(todayKey);
  const [slotEntry, setSlotEntry] = useState<CalendarEntry | null>(null);

  const { start, end } = useMemo(() => rangeFor(view, cursor), [view, cursor]);
  const from = dayKey(start);
  const to = dayKey(end);
  const days = useMemo(() => {
    const out: Date[] = [];
    for (let d = start; d.getTime() <= end.getTime(); d = addDays(d, 1)) out.push(d);
    return out;
  }, [start, end]);

  const cal = useCalendar(from, to);
  const { plans } = usePlans();

  // When the calendar route is not there, the dated items themselves still make a useful calendar.
  const entries = useMemo(() => {
    if (!cal.available) return items.map(entryFromItem).filter((e): e is CalendarEntry => !!e && entryDayKey(e) >= from && entryDayKey(e) <= to);
    // The server also lists rejected pieces (a rejected piece is held so it can't be scheduled); the owner turned those down, so they are not shown.
    const rejected = new Set(items.filter(isRejected).map((i) => i.id));
    return cal.entries.filter((e) => !(e.contentItemId != null && rejected.has(e.contentItemId)));
  }, [cal.available, cal.entries, items, from, to]);

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>();
    for (const e of entries) {
      const k = entryDayKey(e);
      if (!k) continue;
      map.set(k, [...(map.get(k) ?? []), e]);
    }
    map.forEach((list, k) => map.set(k, list.slice().sort(sortEntries)));
    return map;
  }, [entries]);

  const inRangeCount = entries.length;
  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const weekLabel = `${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;

  const step = (dir: -1 | 1) => {
    setCursor((c) => (view === "month" ? new Date(c.getFullYear(), c.getMonth() + dir, 1) : addDays(c, 7 * dir)));
    setSelected(null);
  };
  const goToday = () => {
    setCursor(new Date());
    setSelected(todayKey);
  };

  const open = (e: CalendarEntry) => {
    if (e.contentItemId != null) return onOpenItem(e.contentItemId);
    if (e.kind === "slot") setSlotEntry(e);
  };

  const found = slotEntry ? findSlot(plans, slotEntry.slotId, slotEntry.planId) : null;
  const slot: PlanSlot | null = slotEntry
    ? found?.slot ?? {
        slotId: slotEntry.slotId ?? "",
        date: entryDayKey(slotEntry),
        channel: (PLAN_CHANNELS.includes(slotEntry.channel as PlanChannel) ? slotEntry.channel : "blog") as PlanChannel,
        topic: slotEntry.title,
        angle: "",
        targetQuery: "",
        kind: "new",
        status: calStateOf(slotEntry) === "skipped" ? "skipped" : calStateOf(slotEntry) === "ordered" ? "ordered" : "planned",
      }
    : null;

  const selectedList = selected ? byDay.get(selected) ?? [] : [];

  return (
    <div className="space-y-4" data-testid="calendar-tab" data-view={view}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-foreground" data-testid="cal-range-label">{view === "month" ? monthLabel : weekLabel}</h2>
          <p className="text-xs text-muted-foreground" data-testid="cal-count">
            {inRangeCount === 0 ? `Nothing on the calendar this ${view}.` : `${inRangeCount} item${inRangeCount === 1 ? "" : "s"} this ${view}.`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-md border border-border" role="group" aria-label="Calendar view">
            {(["week", "month"] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                aria-pressed={view === v}
                data-testid={`cal-view-${v}`}
                className={`px-3 py-1.5 text-sm ${view === v ? "bg-blue-600 text-white" : "text-muted-foreground hover:text-foreground"}`}
              >
                {v === "week" ? "Week" : "Month"}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Button type="button" variant="outline" size="icon" aria-label={view === "month" ? "Previous month" : "Previous week"} onClick={() => step(-1)} data-testid="cal-prev">
              <ChevronLeft size={16} />
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={goToday} data-testid="cal-today">Today</Button>
            <Button type="button" variant="outline" size="icon" aria-label={view === "month" ? "Next month" : "Next week"} onClick={() => step(1)} data-testid="cal-next">
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5" aria-label="What the colours mean" data-testid="cal-legend">
        {LEGEND.map((s) => (
          <span key={s} className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium ${CAL_STATE_CLASS[s]}`}>
            {CAL_STATE_LABEL[s]}
          </span>
        ))}
      </div>

      {!cal.available && (
        <p className="rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground" data-testid="cal-fallback-note">
          This shows what is scheduled and published. Planned slots from a content plan appear here once plans are available.
        </p>
      )}

      {cal.isLoading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="mr-2 animate-spin" size={18} /> Loading…
        </div>
      ) : cal.isError ? (
        <div className="space-y-3 py-12 text-center" data-testid="cal-error">
          <p className="text-sm text-foreground">Couldn't load the calendar.</p>
          <Button type="button" variant="outline" size="sm" onClick={() => cal.refetch()}>Try again</Button>
        </div>
      ) : view === "month" ? (
        <>
          <MonthGrid days={days} month={cursor.getMonth()} byDay={byDay} selected={selected} onSelect={(k) => setSelected(selected === k ? null : k)} onOpen={open} todayKey={todayKey} />
          {selected ? (
            <section className="space-y-2" data-testid="cal-selected-day">
              <h3 className="text-sm font-semibold text-foreground">{fmtDayHeading(parseDayKey(selected))}</h3>
              {selectedList.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing on this day.</p>
              ) : (
                selectedList.map((e, i) => <EventRow key={`${e.slotId ?? e.contentItemId ?? i}`} entry={e} item={itemFor(e, items)} onOpen={open} />)
              )}
            </section>
          ) : (
            <p className="text-xs text-muted-foreground">Pick a day to see its pieces.</p>
          )}
        </>
      ) : (
        <div className="space-y-4" data-testid="cal-week">
          {days.map((date) => {
            const k = dayKey(date);
            const list = byDay.get(k) ?? [];
            const isToday = k === todayKey;
            return (
              <section key={k} className="space-y-2" data-testid={`cal-week-day-${k}`} data-today={isToday || undefined}>
                <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <span className={isToday ? "text-blue-300" : undefined}>{fmtDayHeading(date)}</span>
                  {isToday && <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold normal-case tracking-normal text-white">Today</span>}
                </h3>
                {list.length === 0 ? (
                  <p className={`rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground ${isToday ? "border-blue-500/40" : ""}`}>Nothing planned.</p>
                ) : (
                  list.map((e, i) => <EventRow key={`${e.slotId ?? e.contentItemId ?? i}`} entry={e} item={itemFor(e, items)} onOpen={open} />)
                )}
              </section>
            );
          })}
        </div>
      )}

      {!cal.isLoading && !cal.isError && inRangeCount === 0 && (
        <EmptyState title="Nothing is planned for this period.">
          Draper's team writes to a content plan.{" "}
          <button type="button" onClick={onGoPlan} className="text-blue-400 underline underline-offset-2" data-testid="cal-go-plan">
            Open the Plan tab
          </button>{" "}
          to create or approve one.
        </EmptyState>
      )}

      <SlotDialog
        open={slotEntry !== null}
        onOpenChange={(o) => !o && setSlotEntry(null)}
        slot={slot}
        plan={found?.plan ?? null}
        editable={false}
        onOpenItem={(id) => {
          setSlotEntry(null);
          onOpenItem(id);
        }}
      />
    </div>
  );
}
